"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityInput = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_2 = require("react");
const react_intl_1 = require("react-intl");
const notistack_1 = require("notistack");
const react_hook_form_1 = require("react-hook-form");
const material_1 = require("@mui/material");
const mui_form_1 = require("@framework/mui-form");
const mui_page_layout_1 = require("@framework/mui-page-layout");
const provider_api_1 = require("@framework/provider-api");
const provider_test_id_1 = require("@framework/provider-test-id");
const react_hooks_1 = require("@framework/react-hooks");
const EntityInput = props => {
    const { name, controller, getTitle, multiple, autoselect = false, dirtyAutoselect = true, data = {}, variant = "standard", onChange, label, placeholder, disabled, readOnly, disableClear, } = props;
    const suffix = name.split(".").pop();
    const { registerInput, unregisterInput } = (0, mui_form_1.useInputRegistry)();
    const { testId } = (0, provider_test_id_1.useTestId)();
    const testIdProps = testId ? { "data-testid": `${testId}-${name}` } : {};
    const [open, setOpen] = (0, react_2.useState)(false);
    const form = (0, react_hook_form_1.useFormContext)();
    const error = (0, react_hook_form_1.get)(form.formState.errors, name);
    const value = (0, react_hook_form_1.useWatch)({ name });
    const { formatMessage } = (0, react_intl_1.useIntl)();
    const localizedLabel = label ?? formatMessage({ id: `form.labels.${suffix}` });
    const localizedPlaceholder = placeholder ?? formatMessage({ id: `form.placeholders.${suffix}` });
    const localizedHelperText = error ? formatMessage({ id: error.message }, { label: localizedLabel }) : "";
    const [isLoading, setIsLoading] = (0, react_2.useState)(false);
    const [options, setOptions] = (0, react_2.useState)([]);
    const api = (0, provider_api_1.useApi)();
    const abortController = new AbortController();
    const fetchOptions = (0, react_2.useCallback)(async () => {
        setIsLoading(true);
        return api
            .fetchJson({
            url: `/${controller}/autocomplete`,
            data,
            signal: abortController.signal,
        })
            .then((json) => {
            setOptions(json);
            if (autoselect) {
                const newValue = multiple ? [json[0]] : json[0];
                if (!newValue) {
                    form.setValue(name, null, { shouldDirty: dirtyAutoselect });
                    return;
                }
                const isValueNotExistInOptions = value &&
                    (multiple
                        ? value.some((v) => json.every((o) => o.id !== v))
                        : json.every((o) => o.id !== value));
                if (!value || isValueNotExistInOptions) {
                    onChange
                        ? onChange({}, newValue, "autoselect")
                        : form.setValue(name, multiple ? newValue.map((o) => o.id) : newValue.id, {
                            shouldDirty: dirtyAutoselect,
                        });
                }
            }
        })
            .catch(e => {
            if (!e.message.includes("The user aborted a request")) {
                console.error(e);
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: "snackbar.error" }), { variant: "error" });
            }
        })
            .finally(() => {
            setIsLoading(false);
        });
    }, [data, value, name, multiple, form]);
    (0, react_hooks_1.useDeepCompareEffect)(() => {
        void fetchOptions();
        return () => abortController.abort();
    }, [data]);
    (0, react_2.useEffect)(() => {
        registerInput(name, true);
        return () => {
            unregisterInput(name);
        };
    }, [name]);
    return ((0, jsx_runtime_1.jsx)(react_hook_form_1.Controller, { name: name, control: form.control, render: ({ field }) => {
            if (multiple) {
                return ((0, jsx_runtime_1.jsx)(mui_page_layout_1.ProgressOverlay, { isLoading: isLoading, children: (0, jsx_runtime_1.jsx)(material_1.Autocomplete, { open: open, onOpen: () => !readOnly && setOpen(true), onClose: () => setOpen(false), disableClearable: disableClear || readOnly, sx: { my: 1 }, multiple: true, disabled: disabled, options: options, 
                        // preserve order
                        value: value
                            .map((v) => options.find(o => o.id === v))
                            .filter((e) => e), 
                        // value={options.filter((option: IAutocompleteOption) => value.includes(option.id) as boolean)}
                        onChange: onChange ||
                            ((_event, options) => {
                                const value = options ? options.map((option) => option.id) : [];
                                form.setValue(name, value, { shouldDirty: true, shouldTouch: true });
                            }), getOptionLabel: (option) => (getTitle ? getTitle(option) : option.title), renderOption: (props, option) => {
                            const title = getTitle ? getTitle(option) : option.title;
                            return ((0, react_1.createElement)("li", { ...props, key: option.id }, title));
                        }, renderInput: (params) => ((0, jsx_runtime_1.jsx)(material_1.TextField, { ...field, ...params, inputProps: { ...params.inputProps, readOnly, ...testIdProps }, InputProps: { ...params.InputProps }, label: localizedLabel, placeholder: formatMessage({ id: `form.placeholders.${suffix}` }), error: !!error, helperText: localizedHelperText, variant: variant, onChange: () => { }, onBlur: () => {
                                if (!readOnly) {
                                    field.onBlur();
                                }
                            }, fullWidth: true })) }) }));
            }
            else {
                return ((0, jsx_runtime_1.jsx)(mui_page_layout_1.ProgressOverlay, { isLoading: isLoading, children: (0, jsx_runtime_1.jsx)(material_1.Autocomplete, { open: open, onOpen: () => !readOnly && setOpen(true), onClose: () => setOpen(false), disableClearable: disableClear || readOnly, sx: { my: 1 }, multiple: false, disabled: disabled, options: options, value: options.find((option) => value === option.id) || null, onChange: onChange ||
                            ((_event, option) => {
                                const value = option ? option.id : void 0;
                                form.setValue(name, value, { shouldDirty: true, shouldTouch: true });
                            }), getOptionLabel: (option) => (getTitle ? getTitle(option) : option.title), renderOption: (props, option) => {
                            const title = getTitle ? getTitle(option) : option.title;
                            return ((0, react_1.createElement)("li", { ...props, key: option.id }, title));
                        }, renderInput: (params) => ((0, jsx_runtime_1.jsx)(material_1.TextField, { ...field, ...params, inputProps: { ...params.inputProps, readOnly, ...testIdProps }, InputProps: { ...params.InputProps }, label: localizedLabel, placeholder: localizedPlaceholder, error: !!error, helperText: localizedHelperText, variant: variant, onChange: () => { }, onBlur: () => {
                                if (!readOnly) {
                                    field.onBlur();
                                }
                            }, fullWidth: true })) }) }));
            }
        } }));
};
exports.EntityInput = EntityInput;
//# sourceMappingURL=index.js.map